import { useState, useMemo, useRef, useEffect, useCallback, lazy, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Search, X, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { trackGuestActivity } from "@/lib/guestTracking";
import { Skeleton } from "@/components/ui/skeleton";
import { prefetchBrowseData, prefetchCourseData } from "@/hooks/usePrefetchRoute";
import type { HomeData, QuizLearnSetBasic } from "@/hooks/useHomeData";

// Lazy load the What Would You Do component
const WhatWouldYouDo = lazy(() => import("./WhatWouldYouDo").then(m => ({ default: m.WhatWouldYouDo })));

interface SearchResult {
  type: "quiz" | "course" | "flashcard";
  id: string;
  title: string;
  courseId?: string;
  courseTitle?: string;
}

type Course = HomeData["courses"][number];

interface HomeSearchProps {
  showQuiz?: boolean;
  showSearch?: boolean;
  courses?: Course[];
  quizLearnSets?: QuizLearnSetBasic[];
  endlessQuizQuestions?: HomeData["endlessQuizQuestions"];
  popularTags?: string[] | null;
  isLoading?: boolean;
}

/**
 * Home search - now receives courses/quizzes/tags from parent via useHomeData.
 * Flashcard search is deferred until user interacts with search input.
 */
export function HomeSearch({ showQuiz = false, showSearch = true, courses, quizLearnSets, endlessQuizQuestions, popularTags, isLoading }: HomeSearchProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Prefetch browse data on mount for faster navigation
  const handleBrowsePrefetch = useCallback(() => {
    prefetchBrowseData(queryClient);
  }, [queryClient]);

  // Defer heavy flashcard/tag fetches until user focuses search input
  const [hasInteracted, setHasInteracted] = useState(false);

  // Fetch all unique tags from flashcards - deferred until interaction
  const { data: allTags } = useQuery({
    queryKey: ["all-flashcard-tags"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("flashcards")
        .select("tags, chapters!inner(courses!inner(is_published))")
        .not("tags", "is", null);
      
      if (error) throw error;
      
      const tagSet = new Set<string>();
      data?.forEach((card: any) => {
        if (card.chapters?.courses?.is_published && card.tags) {
          card.tags.forEach((tag: string) => {
            if (tag.trim()) tagSet.add(tag.trim().toLowerCase());
          });
        }
      });
      return Array.from(tagSet).sort();
    },
    staleTime: 1000 * 60 * 10,
    enabled: hasInteracted, // Only fetch after user interacts with search
  });

  // Top tags from actual content — matches real course/quiz tags
  const topCourseTags = ["ai", "feedback", "career", "meetings", "productivity", "data-literacy", "soft-skills", "focus"];

  // Fetch flashcards for search - deferred until user interacts
  const { data: flashcards } = useQuery({
    queryKey: ["searchable-flashcards"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("flashcards")
        .select(`
          id, title, tags, chapter_id,
          chapters!inner(id, course_id, courses!inner(id, title, is_published))
        `)
        .not("chapter_id", "is", null)
        .limit(500);
      
      if (error) throw error;
      return data?.filter((f: any) => f.chapters?.courses?.is_published) ?? [];
    },
    staleTime: 1000 * 60 * 5,
    enabled: hasInteracted, // Only fetch after user interacts with search
  });

  // Tag autocomplete suggestions
  const tagSuggestions = useMemo(() => {
    if (!query || !allTags) return [];
    const lowerQuery = query.toLowerCase();
    return allTags
      .filter((tag) => tag.includes(lowerQuery))
      .slice(0, 5);
  }, [query, allTags]);

  // Search results - include quiz sets first
  const searchResults = useMemo((): SearchResult[] => {
    if (!query.trim()) return [];
    const lowerQuery = query.toLowerCase();
    const results: SearchResult[] = [];

    // Search quiz sets first
    quizLearnSets?.forEach((set) => {
      const matchesTitle = set.title.toLowerCase().includes(lowerQuery);
      const matchesDescription = set.description?.toLowerCase().includes(lowerQuery);

      if (matchesTitle || matchesDescription) {
        results.push({
          type: "quiz",
          id: set.id,
          title: set.title,
        });
      }
    });

    // Search courses by title, description, or tags
    courses?.forEach((course) => {
      const matchesTitle = course.title.toLowerCase().includes(lowerQuery);
      const matchesDescription = course.description?.toLowerCase().includes(lowerQuery);
      const matchesTag = (course.tags || []).some((t) =>
        t.toLowerCase().includes(lowerQuery)
      );

      if (matchesTitle || matchesDescription || matchesTag) {
        results.push({
          type: "course",
          id: course.id,
          title: course.title,
        });
      }
    });

    // Search flashcards by title or tags
    flashcards?.forEach((card: any) => {
      const matchesTitle = card.title.toLowerCase().includes(lowerQuery);
      const matchesTag = card.tags?.some((t: string) =>
        t.toLowerCase().includes(lowerQuery)
      );
      
      if (matchesTitle || matchesTag) {
        results.push({
          type: "flashcard",
          id: card.id,
          title: card.title,
          courseId: card.chapters?.courses?.id,
          courseTitle: card.chapters?.courses?.title,
        });
      }
    });

    return results.slice(0, 8);
  }, [query, courses, quizLearnSets, flashcards]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleTagClick = (tag: string) => {
    setQuery(tag);
    setShowResults(true);
    setSubmittedQuery(tag);
    setIsFocused(false);
    inputRef.current?.blur();
    trackGuestActivity("search", { search_query: tag });
  };

  const handleSearch = () => {
    if (query.trim()) {
      setShowResults(true);
      setSubmittedQuery(query.trim());
      setIsFocused(false);
      inputRef.current?.blur();
      // Track search activity
      trackGuestActivity("search", { search_query: query.trim() });
    }
  };

  const handleSuggestionClick = (result: SearchResult) => {
    // Track search activity when clicking suggestion
    if (query.trim()) {
      trackGuestActivity("search", { search_query: query.trim() });
    }
    // On suggestion click, show results and navigate with search query
    setShowResults(true);
    setIsFocused(false);
    const searchParam = query.trim() ? `?q=${encodeURIComponent(query.trim())}` : "";
    if (result.type === "quiz") {
      navigate(`/quiz-learn?set=${result.id}`);
    } else if (result.type === "course") {
      navigate(`/course/${result.id}${searchParam}`);
    } else if (result.courseId) {
      navigate(`/course/${result.courseId}${searchParam}`);
    }
    setQuery("");
    setShowResults(false);
  };

  const handleResultClick = (result: SearchResult) => {
    const searchParam = query.trim() ? `?q=${encodeURIComponent(query.trim())}` : "";
    if (result.type === "quiz") {
      navigate(`/quiz-learn?set=${result.id}`);
    } else if (result.type === "course") {
      navigate(`/course/${result.id}${searchParam}`);
    } else if (result.courseId) {
      navigate(`/course/${result.courseId}${searchParam}`);
    }
    setQuery("");
    setShowResults(false);
    setSubmittedQuery("");
    setIsFocused(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Show autocomplete dropdown while typing
  // Show suggestions when: focused, has query, has results, and either no search submitted OR query changed since last search
  const [submittedQuery, setSubmittedQuery] = useState("");
  const queryChangedSinceSearch = showResults && query.trim().toLowerCase() !== submittedQuery.toLowerCase();
  const showAutocomplete = isFocused && query.trim().length > 0 && searchResults.length > 0 && (!showResults || queryChangedSinceSearch);

  return (
    <div ref={containerRef} className="w-full space-y-5">
      {/* What Would You Do section */}
      {showQuiz && (
        <Suspense fallback={<Skeleton className="h-48 w-full rounded-xl" />}>
          <WhatWouldYouDo 
            scenarios={endlessQuizQuestions}
            isLoading={isLoading}
          />
        </Suspense>
      )}

      {/* Search container with permanent search button */}
      {showSearch && (
        <div className="relative space-y-2">
          <div className="bg-card/80 backdrop-blur-sm border-2 border-primary/50 rounded-xl shadow-md overflow-hidden transition-all duration-200 hover:shadow-lg hover:border-primary/70">
            {/* Search input row with search button */}
            <div className="flex items-center gap-2 p-2.5">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder="Search topics..."
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    if (!e.target.value) setShowResults(false);
                  }}
                  onFocus={() => {
                    setIsFocused(true);
                    if (!hasInteracted) setHasInteracted(true);
                  }}
                  onKeyDown={handleKeyDown}
                  className="h-10 pl-9 pr-8 rounded-lg border-0 bg-primary/5 text-sm focus-visible:ring-1 focus-visible:ring-primary/30 focus-visible:ring-offset-0"
                />
                {query && (
                  <button
                    onClick={() => {
                      setQuery("");
                      setShowResults(false);
                      setSubmittedQuery("");
                    }}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {/* Permanent search button */}
              <Button
                onClick={handleSearch}
                className="h-10 px-5 shrink-0 rounded-xl bg-primary hover:bg-primary/90 shadow-sm"
              >
                Search
              </Button>
            </div>

            {/* Popular tags - compact, inside search container */}
            {topCourseTags.length > 0 && (
              <div className="px-2.5 pb-2 pt-0 flex items-center gap-1 overflow-x-auto">
                {topCourseTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => handleTagClick(tag)}
                    className="shrink-0 px-2 py-0.5 text-[10px] text-muted-foreground hover:text-primary hover:bg-accent rounded transition-colors"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Browse All button - below search bar with micro-animation */}
          <Link 
            to="/browse"
            className="group w-full h-9 inline-flex items-center justify-center gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-medium text-sm transition-all shadow-sm hover:shadow-md hover:scale-[1.01] active:scale-[0.99]"
            onMouseEnter={handleBrowsePrefetch}
            onTouchStart={handleBrowsePrefetch}
            onFocus={handleBrowsePrefetch}
          >
            Browse all topics
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>

          {/* Autocomplete suggestions dropdown (while typing) */}
          {showAutocomplete && (
            <div className="absolute left-0 right-0 z-50 mt-1.5 bg-card border rounded-lg shadow-lg overflow-hidden animate-fade-in">
              <div className="max-h-80 overflow-y-auto">
                {searchResults.slice(0, 5).map((result, idx) => (
                  <button
                    key={`suggest-${result.type}-${result.id}-${idx}`}
                    onClick={() => handleSuggestionClick(result)}
                    className="w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors flex items-start gap-3 border-b last:border-b-0"
                  >
                    <span className="text-lg shrink-0">
                      {result.type === "quiz" ? "🎯" : result.type === "course" ? "📚" : "💡"}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{result.title}</p>
                      {result.type === "flashcard" && result.courseTitle && (
                        <p className="text-xs text-muted-foreground truncate">
                          in {result.courseTitle}
                        </p>
                      )}
                      {result.type === "quiz" && (
                        <p className="text-xs text-muted-foreground truncate">
                          Quiz & Learn
                        </p>
                      )}
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search results list (after Enter/Search button) */}
      {showSearch && showResults && searchResults.length > 0 && (
        <div className="mt-5 space-y-2.5 animate-fade-in">
          <p className="text-sm text-muted-foreground">
            {searchResults.length} result{searchResults.length !== 1 ? "s" : ""} for "{query}"
          </p>
          {searchResults.map((result, idx) => (
            <button
              key={`result-${result.type}-${result.id}-${idx}`}
              onClick={() => handleResultClick(result)}
              className="group w-full block bg-card border rounded-lg overflow-hidden hover:border-primary/50 transition-all text-left relative"
            >
              {/* Accent bar */}
              <div className={`absolute left-0 top-0 bottom-0 w-1 ${result.type === "quiz" ? "bg-amber-500" : "bg-primary"}`} />

              <div className="p-3 pl-4 flex items-center gap-3">
                {/* Icon */}
                <div className="shrink-0">
                  <span className="text-2xl">
                    {result.type === "quiz" ? "🎯" : result.type === "course" ? "📚" : "💡"}
                  </span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm leading-tight group-hover:text-primary transition-colors truncate">
                    {result.title}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-1">
                    {result.type === "quiz"
                      ? "Quiz & Learn"
                      : result.type === "flashcard" && result.courseTitle
                      ? `in ${result.courseTitle}`
                      : result.type === "course"
                      ? "Course"
                      : "Flashcard"}
                  </p>
                </div>

                {/* Arrow */}
                <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {showSearch && showResults && query.trim().length > 0 && searchResults.length === 0 && (
        <div className="mt-5 bg-card border rounded-lg p-5 text-left relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-muted" />
          <p className="text-muted-foreground text-sm pl-3">No results found for "{query}"</p>
          <Link to="/browse" className="text-primary text-sm hover:underline mt-2 inline-block pl-3">
            Browse all courses instead
          </Link>
        </div>
      )}
    </div>
  );
}
